/**
 * Обёртка над cordova-plugin-advanced-http для нативных HTTP-запросов.
 * Используется в Cordova-окружении для обхода CORS-ограничений WebView.
 */

declare const cordova: {
  plugin: {
    http: {
      get: (
        url: string,
        params: Record<string, any>,
        headers: Record<string, string>,
        success: (response: CordovaHttpResponse) => void,
        failure: (error: CordovaHttpError) => void
      ) => void
      post: (
        url: string,
        data: Record<string, any>,
        headers: Record<string, string>,
        success: (response: CordovaHttpResponse) => void,
        failure: (error: CordovaHttpError) => void
      ) => void
      put: (
        url: string,
        data: Record<string, any>,
        headers: Record<string, string>,
        success: (response: CordovaHttpResponse) => void,
        failure: (error: CordovaHttpError) => void
      ) => void
      delete: (
        url: string,
        params: Record<string, any>,
        headers: Record<string, string>,
        success: (response: CordovaHttpResponse) => void,
        failure: (error: CordovaHttpError) => void
      ) => void
      setDataSerializer: (serializer: 'urlencoded' | 'json' | 'utf8') => void
    }
  }
}

interface CordovaHttpResponse {
  status: number
  data: string
  headers: Record<string, string>
}

interface CordovaHttpError {
  status: number
  error: string
  headers?: Record<string, string>
}


/**
 * Проверяет, доступен ли cordova-plugin-advanced-http
 */
export const isCordovaHttpAvailable = (): boolean => {
  return (
    typeof cordova !== 'undefined' &&
    cordova.plugin &&
    typeof cordova.plugin.http !== 'undefined'
  )
}


/**
 * Выполняет HTTP-запрос через нативный плагин Cordova
 */
export const cordovaHttpRequest = <T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  data?: Record<string, any>,
  headers: Record<string, string> = {},
): Promise<T> => {
  return new Promise((resolve, reject) => {
    const httpMethod = method.toLowerCase() as 'get' | 'post' | 'put' | 'delete'

    // Для POST/PUT устанавливаем JSON serializer
    if (method === 'POST' || method === 'PUT') {
      cordova.plugin.http.setDataSerializer('json')
    }

    const defaultHeaders: Record<string, string> = {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
      ...headers,
    }

    // Для POST/PUT добавляем Content-Type
    if ((method === 'POST' || method === 'PUT') && data) {
      defaultHeaders['Content-Type'] = 'application/json'
    }

    const onSuccess = (response: CordovaHttpResponse) => {
      try {
        const parsed = response.data ? JSON.parse(response.data) : null
        resolve(parsed as T)
      } catch {
        // Если не JSON, возвращаем как есть
        resolve(response.data as unknown as T)
      }
    }

    const onError = (error: CordovaHttpError) => {
      const err = new Error(error.error || `HTTP Error ${error.status}`)
      ;(err as any).status = error.status
      ;(err as any).response = { status: error.status, data: error.error }
      reject(err)
    }

    cordova.plugin.http[httpMethod](
      url,
      data || {},
      defaultHeaders,
      onSuccess,
      onError,
    )
  })
}


/**
 * POST-запрос с URL-encoded данными (для OLD API)
 */
export const cordovaHttpPostUrlEncoded = <T>(
  url: string,
  data: string,
  headers: Record<string, string> = {},
): Promise<T> => {
  return new Promise((resolve, reject) => {
    cordova.plugin.http.setDataSerializer('utf8')

    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...headers,
    }

    cordova.plugin.http.post(
      url,
      { body: data } as any,
      defaultHeaders,
      (response) => {
        try {
          const parsed = response.data ? JSON.parse(response.data) : null
          resolve(parsed as T)
        } catch {
          resolve(response.data as unknown as T)
        }
      },
      (error) => {
        const err = new Error(error.error || `HTTP Error ${error.status}`)
        ;(err as any).status = error.status
        reject(err)
      },
    )
  })
}
